import { z } from 'zod';
import DOMPurify from 'dompurify';
import i18n from '@/i18n';

// Resolve a translation lazily. Used inside refine/superRefine callbacks so
// the message is evaluated at validation time, not at schema-creation time.
const tr = (key: string): string => i18n.t(key);

// ============================================
// INPUT SANITIZATION (XSS Protection)
// ============================================

/**
 * Sanitize user input to prevent XSS attacks
 */
export function sanitizeInput(input: string): string {
  // No "<" → there is no markup to strip; return the text exactly as typed
  // (decoding would turn "&region" / "&not12" into "®ion" / "¬12").
  if (!input.includes('<')) return input.trim();
  const stripped = DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [], // Strip all HTML
    ALLOWED_ATTR: [],
  });
  // DOMPurify returns serialized HTML, so "&" comes back as "&amp;" and "<" as
  // "&lt;". This is plain text (React escapes it on render) — keep exactly the
  // characters the host typed: a WiFi password "pa&ss" stored as "pa&amp;ss"
  // made guests copy the wrong password.
  let text = decodeEntities(stripped);
  // Decoding can turn typed entities ("&lt;b&gt;") into markup — strip again
  // until nothing changes; if markup somehow survives, drop the brackets.
  for (let i = 0; i < 10 && /<[a-z!/?]/i.test(text); i++) {
    const next = decodeEntities(DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }));
    if (next === text) break;
    text = next;
  }
  if (/<[a-z!/?]/i.test(text)) text = text.replace(/[<>]/g, '');
  return text.trim();
}

function decodeEntities(s: string): string {
  if (!s.includes('&')) return s;
  if (typeof document !== 'undefined') {
    const ta = document.createElement('textarea'); // never executes markup
    ta.innerHTML = s;
    return ta.value;
  }
  return s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&');
}

/**
 * Sanitize HTML content (for rich text)
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: [],
  });
}

// ============================================
// VALIDATION SCHEMAS
// ============================================

// Common schemas. Default Zod messages remain in English; for the most
// user-facing messages we route through `tr()` so they follow the active UI
// language at validation time. Schema messages are resolved lazily inside
// .refine()/.superRefine() callbacks so language changes propagate correctly.
export const emailSchema = z
  .string()
  .email()
  .max(255)
  .transform(sanitizeInput);

export const phoneSchema = z
  .string()
  .min(6)
  .max(20)
  .superRefine((val, ctx) => {
    if (!/^[+]?[\d\s\-()]+$/.test(val) || val.replace(/[^\d]/g, '').length < 7) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: tr('validation.invalidPhoneNumber'),
      });
    }
  })
  .optional()
  .transform((val) => (val ? sanitizeInput(val) : undefined));

export const nameSchema = z
  .string()
  .min(1)
  .max(100)
  .transform(sanitizeInput);

export const textSchema = z
  .string()
  .max(5000)
  .transform(sanitizeInput);

export const shortTextSchema = z
  .string()
  .max(500)
  .transform(sanitizeInput);

// Access code (6 characters, alphanumeric).
export const accessCodeSchema = z
  .string()
  .length(6)
  .superRefine((val, ctx) => {
    if (!/^[A-Z0-9]+$/.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: tr('validation.invalidAccessCode'),
      });
    }
  });

// UUID validation
export const uuidSchema = z.string().uuid();

// Date validation
export const dateSchema = z.string().superRefine((val, ctx) => {
  if (isNaN(new Date(val).getTime())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: tr('validation.invalidDate'),
    });
  }
});

// Future date validation
export const futureDateSchema = z.string().superRefine((val, ctx) => {
  const date = new Date(val);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  if (isNaN(date.getTime()) || date < now) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: tr('validation.invalidDate'),
    });
  }
});

// ============================================
// PROPERTY VALIDATION
// ============================================

export const propertySchema = z.object({
  name: nameSchema,
  type: z.enum(['apartment', 'house', 'villa', 'room', 'studio']),
  description: textSchema.optional(),
  address: z.string().min(1).max(255).transform(sanitizeInput),
  city: z.string().min(1).max(100).transform(sanitizeInput),
  country: z.string().min(1).max(100).transform(sanitizeInput),
  postal_code: z.string().max(20).optional().transform((val) => (val ? sanitizeInput(val) : undefined)),
});

// ============================================
// UNIT VALIDATION
// ============================================

export const unitSchema = z.object({
  property_id: uuidSchema,
  name: nameSchema,
  description: textSchema.optional(),
  floor: z.number().int().min(-5).max(200).optional(),
  bedrooms: z.number().int().min(0).max(50),
  bathrooms: z.number().int().min(0).max(50),
  max_guests: z.number().int().min(1).max(100),
  size_sqm: z.number().min(1).max(10000).optional(),
  base_price: z.number().min(0).max(100000),
  cleaning_fee: z.number().min(0).max(10000).optional(),
  amenities: z.array(z.string().max(50)).max(50).optional(),
  wifi_name: shortTextSchema.optional(),
  wifi_password: shortTextSchema.optional(),
  check_in_instructions: textSchema.optional(),
  house_rules: textSchema.optional(),
});

// ============================================
// BOOKING VALIDATION
// ============================================

export const bookingSchema = z.object({
  unit_id: uuidSchema,
  guest_name: nameSchema,
  guest_email: emailSchema,
  guest_phone: phoneSchema,
  check_in: futureDateSchema,
  check_out: dateSchema,
  guests_count: z.number().int().min(1).max(100),
  total_price: z.number().min(0).max(1000000),
  notes: textSchema.optional(),
}).superRefine((data, ctx) => {
  const checkIn = new Date(data.check_in);
  const checkOut = new Date(data.check_out);
  if (checkOut <= checkIn) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: tr('validation.checkOutBeforeCheckIn'),
      path: ['check_out'],
    });
  }
});

// ============================================
// MESSAGE VALIDATION
// ============================================

export const messageSchema = z.object({
  booking_id: uuidSchema,
  content: z.string().min(1).max(2000).transform(sanitizeInput),
  sender_type: z.enum(['landlord', 'guest']),
});

// ============================================
// GUEST ACCESS VALIDATION
// ============================================

// Guest access uses only UUID token for security (122-bit entropy)
export const guestAccessSchema = z.object({
  token: uuidSchema,
});

// ============================================
// RATE LIMITING HELPER
// ============================================

const rateLimitStore = new Map<string, { count: number; resetAt: number }>();

export function checkRateLimit(
  key: string,
  maxRequests: number = 100,
  windowMs: number = 60000
): { allowed: boolean; remaining: number; resetIn: number } {
  const now = Date.now();
  const entry = rateLimitStore.get(key);

  if (!entry || now > entry.resetAt) {
    rateLimitStore.set(key, { count: 1, resetAt: now + windowMs });
    return { allowed: true, remaining: maxRequests - 1, resetIn: windowMs };
  }

  if (entry.count >= maxRequests) {
    return { allowed: false, remaining: 0, resetIn: entry.resetAt - now };
  }

  entry.count++;
  return { allowed: true, remaining: maxRequests - entry.count, resetIn: entry.resetAt - now };
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Generate a secure random access code
 */
export function generateAccessCode(length: number = 6): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Removed confusing chars (0, O, 1, I)
  let code = '';
  const randomValues = new Uint32Array(length);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < length; i++) {
    code += chars[randomValues[i] % chars.length];
  }

  return code;
}

/**
 * Generate a secure UUID v4
 */
export function generateUUID(): string {
  return crypto.randomUUID();
}

/**
 * Hash text using SHA-256 (for privacy)
 */
export async function hashText(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
